import { useMemo } from 'react';

import type { Address, DeliveryZoneCoverage } from '~/api/DeliveryAddress';
import { DeliveryMethods } from '~/api/ETA';
import type { PickupPoint } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import { getZoneCentroid } from '~/components/ModalDeliveryAddress/GMap/utils';
import { useDeliveryZones } from '~/components/ModalEditAddressV3/hooks/useDeliveryZones';
import { resolveAddressUsage } from '~/utils/addressUsage';
import { getDistanceInMeters } from '~/utils/geo';

export type { PickupPoint };

export { getPickupPointId } from '~/components/ModalDeliveryAddress/GMap/utils';

/**
 * Display name of a pickup point — the delivery zone name, the same field the
 * PUDO markers use as their title. Falls back to the address so a zone with no
 * name still renders a label instead of an empty line.
 */
export function getPickupName(point: PickupPoint): string {
  return point.name || point.address;
}

/**
 * Physical warehouse address of a pickup point, shown as the secondary line
 * under the name in the pickup list.
 */
export function getPickupAddress(point: PickupPoint): string {
  return point.address;
}

/**
 * Extracts click & collect pickup points from delivery zone coverages.
 * A zone is a pickup point when its delivery method shipping type is
 * CLICK_AND_COLLECT. The point's location and label come from the warehouse
 * (its coordinates and address), falling back to the zone centroid / name when
 * the warehouse doesn't carry them.
 *
 * One physical pickup point per warehouse: a warehouse may expose several
 * click-and-collect zones (different assortments/schedules) that all resolve to
 * the same address and coordinates, so they are collapsed by warehouse code —
 * otherwise the list and map render visually identical duplicate entries. The
 * first pickup zone encountered for a warehouse wins.
 */
export function selectPickupPoints(
  coverages: DeliveryZoneCoverage[],
): PickupPoint[] {
  const pointsByWarehouse = new Map<string, PickupPoint>();

  for (const coverage of coverages) {
    const { warehouse } = coverage;

    for (const zone of coverage.zones) {
      if (
        zone?.deliveryMethod.shippingType !== DeliveryMethods.CLICK_AND_COLLECT
      ) {
        continue;
      }

      if (pointsByWarehouse.has(warehouse.code)) {
        continue;
      }

      const center =
        warehouse.latitude != null && warehouse.longitude != null
          ? { lat: warehouse.latitude, lng: warehouse.longitude }
          : getZoneCentroid(zone);

      pointsByWarehouse.set(warehouse.code, {
        zone,
        center,
        name: zone.name || '',
        address: warehouse.address || zone.description || zone.name,
        capacity: zone.capacity ?? null,
        warehouseCode: warehouse.code,
      });
    }
  }

  return Array.from(pointsByWarehouse.values());
}

/**
 * Finds the pickup point for a saved address: prefers an exact warehouse-code
 * match, otherwise the point whose center is nearest the address coordinates.
 * Returns undefined when there are no points.
 */
export function findPickupPointForAddress(
  points: PickupPoint[],
  address: Address,
): PickupPoint | undefined {
  if (!points.length) {
    return undefined;
  }

  if (address.warehouse_code) {
    const byCode = points.find(
      (point) => point.warehouseCode === address.warehouse_code,
    );
    if (byCode) {
      return byCode;
    }
  }

  const target = { lat: address.latitude, lng: address.longitude };

  return points.reduce((nearest, point) =>
    getDistanceInMeters(point.center, target) <
    getDistanceInMeters(nearest.center, target)
      ? point
      : nearest,
  );
}

export function usePickupZones(enabled = true) {
  const { data: coverages, isLoading } = useDeliveryZones(enabled);

  const pickupPoints = useMemo(
    () => sortPickupPointsByCapacity(selectPickupPoints(coverages ?? [])),
    [coverages],
  );

  return { pickupPoints, isLoading };
}

const capacityOf = (point?: PickupPoint): number => point?.capacity ?? -1;

/**
 * Orders pickup points by assortment size, largest first — the order the
 * pickup list and the map markers are rendered in. Points whose zone carries
 * no assortment size sort last, and equal sizes keep the backend's order.
 */
export function sortPickupPointsByCapacity(
  points: PickupPoint[],
): PickupPoint[] {
  return [...points].sort(
    (left, right) => capacityOf(right) - capacityOf(left),
  );
}

/**
 * Pickup point a saved address was created at, or undefined when nothing
 * identifies one (zones are also unavailable during SSR, before the query
 * resolves).
 *
 * Deliberately stricter than {@link findPickupPointForAddress}: that one falls
 * back to the nearest point at any distance, which is right when the customer
 * is picking a point on a map and wrong when we are labelling a record they
 * already saved — an address whose warehouse code was never stored would
 * silently take the name and assortment of whichever point happens to be
 * closest. Here an exact warehouse-code match is the only match.
 *
 * The usage check guards the other direction: a courier address carries the
 * code of the warehouse serving it, and that warehouse may also run a pickup
 * zone, which would otherwise label a home address as a pickup point.
 */
export function findPickupPointForSavedAddress(
  address: Address,
  points: PickupPoint[],
): PickupPoint | undefined {
  if (
    resolveAddressUsage(address.usage) !== 'pickup' ||
    !address.warehouse_code
  ) {
    return undefined;
  }

  return points.find((point) => point.warehouseCode === address.warehouse_code);
}

/**
 * Orders pickup addresses by the assortment size of the point behind them,
 * largest first, leaving delivery addresses untouched at their original
 * positions. Addresses whose point is unknown sort last; equal sizes keep the
 * incoming order.
 */
export function sortPickupAddressesByCapacity(
  addresses: Address[],
  points: PickupPoint[],
): Address[] {
  const slots: number[] = [];
  const ranked: { address: Address; capacity: number }[] = [];

  for (const [index, address] of addresses.entries()) {
    if (resolveAddressUsage(address.usage) === 'pickup') {
      slots.push(index);
      ranked.push({
        address,
        capacity: capacityOf(findPickupPointForSavedAddress(address, points)),
      });
    }
  }

  if (ranked.length < 2) {
    return addresses;
  }

  ranked.sort((left, right) => right.capacity - left.capacity);

  const ordered = [...addresses];

  slots.forEach((slot, index) => {
    const entry = ranked[index];

    if (entry) {
      ordered[slot] = entry.address;
    }
  });

  return ordered;
}
